import React, { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Box, CircularProgress, Typography, Button } from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { debugLogForUser } from '../utils/asyncUtils';

interface ProtectedRouteProps {
  children: ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  const [initialLoad, setInitialLoad] = React.useState(true);
  const [authTimeout, setAuthTimeout] = React.useState(false);
  const [showTroubleshooting, setShowTroubleshooting] = React.useState(false);

  React.useEffect(() => {
    let mounted = true;
    let timeoutId: NodeJS.Timeout;
    let emergencyTimeoutId: NodeJS.Timeout;

    // Debug logging for specific users
    if (user?.email) {
      debugLogForUser(user.email, 'ProtectedRoute:state', { loading, initialLoad, userExists: !!user });
    }

    // Initial load timeout (extended from 1s to 3s)
    if (loading || initialLoad) {
      timeoutId = setTimeout(() => {
        if (mounted) {
          setInitialLoad(false);
          debugLogForUser(user?.email || 'unknown', 'ProtectedRoute:initial_load_timeout');
        }
      }, 3000);

      // Emergency timeout for stuck auth (15 seconds)
      emergencyTimeoutId = setTimeout(() => {
        if (mounted && loading) {
          console.warn('Auth loading stuck for 15 seconds, showing troubleshooting options');
          setAuthTimeout(true);
          setInitialLoad(false);
          debugLogForUser(user?.email || 'unknown', 'ProtectedRoute:emergency_timeout');
        }
      }, 15000);
    }

    return () => {
      mounted = false;
      clearTimeout(timeoutId);
      clearTimeout(emergencyTimeoutId);
    };
  }, [loading, initialLoad, user?.email]);

  // Show emergency troubleshooting if auth is stuck
  if (authTimeout && loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          padding: 4,
          textAlign: 'center'
        }}
      >
        <CircularProgress sx={{ color: 'primary.main', mb: 3 }} />
        <Typography variant="h6" sx={{ mb: 2 }}>
          Authentication is taking longer than expected
        </Typography>
        <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary', maxWidth: 400 }}>
          This may be due to a network issue or session problem. Try refreshing the page or clearing your browser data.
        </Typography>
        
        {!showTroubleshooting ? (
          <Button 
            variant="outlined" 
            onClick={() => setShowTroubleshooting(true)}
            sx={{ mb: 2 }}
          >
            Show Troubleshooting Options
          </Button>
        ) : (
          <Box sx={{ maxWidth: 500 }}>
            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold' }}>
              Troubleshooting Steps:
            </Typography>
            <Box sx={{ textAlign: 'left', mb: 3 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                1. <Button variant="text" size="small" onClick={() => window.location.reload()}>
                  Refresh this page
                </Button>
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                2. Clear browser cache and cookies
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                3. Try logging out and back in
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                4. Try a different browser or incognito mode
              </Typography>
            </Box>
            
            <Button 
              variant="contained" 
              onClick={() => window.location.href = '/login'}
              sx={{ mr: 2 }}
            >
              Go to Login
            </Button>
            <Button 
              variant="outlined" 
              onClick={() => window.location.reload()}
            >
              Refresh Page
            </Button>
          </Box>
        )}
      </Box>
    );
  }

  // Normal loading state (first 3 seconds)
  if (loading && initialLoad) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <CircularProgress sx={{ color: 'primary.main' }} />
      </Box>
    );
  }

  // Not authenticated - redirect to login
  if (!user) {
    debugLogForUser('unknown', 'ProtectedRoute:redirect_to_login', { location: location.pathname });
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Authenticated - show protected content
  debugLogForUser(user.email || '', 'ProtectedRoute:authenticated', { userId: user.id });
  return <>{children}</>;
};

export default ProtectedRoute; 